/**
 * @fileoverview "What this file shows" half of the WIP import review screen.
 *
 * Everything here is stated with its evidence rather than asked, so the
 * questions further down the screen are only the ones the data genuinely
 * cannot settle. A screen that asks about everything gets clicked through.
 */

import type { ReactNode } from "react";
import { Check, X } from "lucide-react";

import type { WipAnalysis } from "@cvx/timesheets/import/wipAnalysis";
import { formatAudExact } from "../format";

const CARD_CLASS = "rounded-xl border border-border/60 bg-background p-5";

export function WipImportReviewFacts({
  analysis,
  fileName,
  sheetName,
}: {
  analysis: WipAnalysis;
  fileName: string;
  sheetName: string;
}) {
  const { dateRange, integrityFailureCount, totals, unit } = analysis;

  return (
    <section className={CARD_CLASS}>
      <h3 className="text-sm font-semibold text-primary">
        What this file shows
      </h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Read from {fileName}. Nothing here is saved — check it against the
        spreadsheet before applying anything.
      </p>

      <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-2">
        <Fact label="Sheet">
          <strong className="font-medium">{sheetName || fileName}</strong>
          <Muted>
            {" "}
            — {analysis.dataRowCount.toLocaleString("en-AU")} rows
            {analysis.skippedBlankRows > 0
              ? `, ${analysis.skippedBlankRows} blank or unnamed skipped`
              : ""}
          </Muted>
        </Fact>

        <Fact label="Matter">
          {analysis.caseNames.length > 0 ? (
            <strong className="font-medium">
              {analysis.caseNames.join(", ")}
            </strong>
          ) : (
            "—"
          )}
        </Fact>

        <Fact label="Billing increment">
          {unit.minutes !== null ? (
            <>
              <strong className="font-medium">{unit.minutes} minutes</strong>
              <Muted>
                {" "}
                — the same on all {unit.usableRows} rows carrying both hours and
                units
              </Muted>
            </>
          ) : (
            <span className="text-destructive">
              Not consistent —{" "}
              {unit.distribution
                .map((d) => `${d.minutes} minutes on ${d.rows} rows`)
                .join(", ")}
            </span>
          )}
        </Fact>

        <Fact label="Entry dates">
          {dateRange ? (
            <strong className="font-medium">
              {dateRange.earliest} to {dateRange.latest}
            </strong>
          ) : (
            <span className="text-destructive">Could not be read</span>
          )}
          <Muted> — {analysis.dateFormatEvidence}</Muted>
        </Fact>

        <Fact label="Work in progress">
          <strong className="font-medium tabular-nums">
            {formatAudExact(totals.netCostCents / 100)}
          </strong>
          <Muted>
            {" "}
            — {formatAudExact(totals.claimCents / 100)} claimable,{" "}
            {formatAudExact(totals.writeOffCents / 100)} written off across{" "}
            {totals.writeOffRows} rows
          </Muted>
        </Fact>

        <Fact label="Row integrity">
          {integrityFailureCount === 0 ? (
            <span className="inline-flex items-start gap-1.5 text-emerald-600 dark:text-emerald-400">
              <Check className="mt-0.5 size-3.5 shrink-0" />
              Net cost equals hours × rate on every row
            </span>
          ) : (
            <span className="inline-flex items-start gap-1.5 text-destructive">
              <X className="mt-0.5 size-3.5 shrink-0" />
              {integrityFailureCount} rows do not balance
            </span>
          )}
        </Fact>
      </dl>
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-primary">{children}</dd>
    </div>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}
